import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from 'dayjs';

import { Col, Flex, Form, Input, Row, Table, Tooltip, Spin, Dropdown, Button, Menu, Space, Alert } from "antd";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { getOverview, getOverviewExport, clearData } from "./dashboardSlice";
import { buildQueryString, debounce } from "../../utils/helpers";
import { HeaderCell } from "./HeaderCell";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { IconDownload } from "../../components/IconDownload/IconDownload";
import { IconSearch } from "../../components/IconSearch/IconSearch";
import { MainButton } from "../../components/MainButton/MainButton";
import { Notification } from "../../components/Notification/Notification";
import { SalesSummary } from "../SalesSummary/SalesSummary";
import { Goods } from "../GoodsIn/GoodsIn";
import { SearchBox } from "../SearchBox/SearchBox";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { CurrentOrders } from "../CurrentOrders/CurrentOrders";
import { SalesOrders } from "../SalesOrders/SalesOrders";
import { CSVLink } from "react-csv";
import { dateCell } from "../../components/DateCell/DateCell";
import { useAuth } from "../../store/AuthContext";
import { useUrlSyncedState } from "./useUrlSyncedState";
import { WarningMessage } from "../../components/WarningMessage/WarningMessage";
import css from "./Dashboard.module.css";

export const Dashboard = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); 
  
  const defaultSortColumn = "Release_Date:desc"
  
  const [showNotification, setShowNotification] = useState(false);

  const [initialSearchValues, setInitialSearchValues] = useState(null);

  const pageButtonRef = React.useRef();
  const exportButtonRef = React.useRef();

  //const page = new URLSearchParams(location.search).get("page") || 1;
  //const search = new URLSearchParams(location.search).get("search");
  //const sort = new URLSearchParams(location.search).get("sort");
  
  const [restored, setRestored] = useState(false);
  const [hadHash, setHadHash] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState({ search: [], format: [], range: { start: null, end: null }});
  const [sortDirection, setSortDirection] = useState(defaultSortColumn);
  
  const [sortField, sortOrder] = sortDirection.split(":");
  const antdSortOrder = sortOrder === "asc" ? "ascend" : "descend";

  useEffect(() => {
    if (!restored) return;
    if (!hadHash) return;
	setInitialSearchValues({
		...search
	})
    dispatch(getOverview({
      page,
      pageSize,
      search,
      sort: sortDirection
    }));
  }, [restored]);  
  
  useUrlSyncedState({
    page: { value: page, set: setPage },
    pageSize: { value: pageSize, set: setPageSize },
    search: { value: search, set: setSearch },
    sortDirection: { value: sortDirection, set: setSortDirection },
    restored: restored, 
    setRestored: setRestored,
    setHadHash: setHadHash
  });

  const getFullData = () => {
    return dispatch(
      getOverviewExport({
        search,
        sort: sortDirection
      }),
    );

  }

  const { overview, getOverviewInProgress } =
    useSelector((state) => state?.dashboard) || {};

  const { overviewExport, getOverviewExportInProgress } =
    useSelector((state) => state?.dashboard) || {};

  const { data: rows, totalPages } = overview || {};

/*
  useEffect(() => {
    const shouldRestore = sessionStorage.getItem("dashboardBackToSearch") === "true";
    if (shouldRestore) {
      const storedSearch = JSON.parse(sessionStorage.getItem("dashboardSearch"));
      if (storedSearch) {
        setSearch(storedSearch);
        setInitialSearchValues(storedSearch);
        setTimeout(() => {onApplySearch(storedSearch);}, 100)
      }
    }
  }, []);
*/ 
  
  useEffect(() => {
    if (overviewExport !== null) {
      exportButtonRef.current.link.click();
    }
  }, [overviewExport]);

  const columns = [
    {
      title: "Artist",
      editable: true,
      dataIndex: "Artist",
      width: 250,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "Artist" ? antdSortOrder : null,
	  render: (text) => (
	    <span className={css.antTableCellClipped} title={text}>
		  {text}
		</span>
	  ),
    },
    {
      title: "Title",
      editable: true,
      dataIndex: "Title",
      width: 250,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "Title" ? antdSortOrder : null,
	  render: (text) => (
		<span className={css.antTableCellClipped} title={text}>
		  {text}
		</span>
	  ),
    },
    {
      title: "Imprint",
      editable: true,
      dataIndex: "Label",
      width: 230,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "Label" ? antdSortOrder : null,
	  render: (text) => (
		<span className={css.antTableCellClipped} title={text}>
		  {text}
		</span>
	  ),
    },    
    
    {
      title: "Catalog Number",
      editable: true,
      dataIndex: "Catalogue_Number",
      sorter: true,
      width: 170,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "Catalogue_Number" ? antdSortOrder : null,
	  render: (text) => (
		<span className={css.antTableCellClipped} title={text}>
		  {text}
		</span>
	  ),
    },
	{
	  title: (
		<Tooltip title="Project Number">
		  <div>Project Number</div>
		</Tooltip>
	  ),
	  editable: true,
	  dataIndex: "Project",
	  sorter: (_, __, sort) =>
		  setSortDirection(`Project:${sort === "ascend" ? "asc" : "desc"}`),
	  width: 150,
	  showSorterTooltip: false,
	  align: "left"	,
	  render: (text) => (
		<span className={css.antTableCellClipped} title={text}>
		  {text}
		</span>
	  ),
	},

    {
      title: (
        <Tooltip title="Physical Format">
          <div>Format</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Format",
      sorter: true,
      width: 100,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "Format" ? antdSortOrder : null,
	  render: (text) => (
		<span className={css.antTableCellClipped} title={text}>
			{text}
		</span>
	  )

    },

    {
      title: (
        <Tooltip title="Barcode (UPC/EAN)">
          <div>UPC</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "UPC",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "UPC" ? antdSortOrder : null,
    },
    {
      title: (
        <Tooltip title="Release Date">
          <div>Release Date</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Release_Date",
      sorter: true,
      width: 135,
      align: "left",
      render: (record) => dateCell(record),
      showSorterTooltip: false,
      sortOrder: sortField === "Release_Date" ? antdSortOrder : null,
    },  
    {
      title: (
        <Tooltip title="Stock on hand at consignment accounts">
          <div>Cons. Stock</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "ConsStock",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "ConsStock" ? antdSortOrder : null,
    },
  
    {
      title: (
        <Tooltip title="Total territory sales to date - direct sales accounts only">
          <div>Direct Sales</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "DirectSales",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "DirectSales" ? antdSortOrder : null,
    },
    {
      title: (
        <Tooltip title="Total territory sales to date - consignment accounts only">
          <div>Cons. Total Sales</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "ConsTotalSales",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "ConsTotalSales" ? antdSortOrder : null,
    },
    {
      title: (
        <Tooltip title="Past seven-day sales total at consignment accounts">
          <div>Cons. 7 Day Sales</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "ConsSevenDaySales",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "ConsSevenDaySales" ? antdSortOrder : null,
    },
    {
      title: (
        <Tooltip title="Total sales to date - direct and consignment accounts combined">
          <div>Total Sales</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "TotalSales",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      sortOrder: sortField === "TotalSales" ? antdSortOrder : null,
    },
    
  ];

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(`/details/${record.UPC}`);
        sessionStorage.setItem("dashboardBackToSearch", "true");
      }
    };
  };
  
  const handleTableChange = (pagination, filters, sorter) => {
    
    let { current, pageSize } = pagination;
    
    let sort = defaultSortColumn;
    if (sorter?.field && sorter?.order) {
      sort = `${sorter.field}:${sorter.order.replace("end", "")}`;
      // Reset page only if sort actually changed
      if (sort !== sortDirection) {
        current = 1;
      }
    } else {
      // if no sorter applied, use current sortDirection
      sort = sortDirection;
    }

      

    setPage(current);
    setPageSize(pageSize);
    setSortDirection(sort);
        
    dispatch(
      getOverview({
        page,
        pageSize, 
        search,
        sort
      })
    );
  };
  
  
  const onApplySearch = (params) => {
    console.log('apply search called');
    setSearch(params);

    setPage(1);
    
    dispatch(
      getOverview({
        page: 1,
        pageSize,
        search: params,
        sort: sortDirection,
      }),
    );
    
    //sessionStorage.setItem("dashboardSearch", JSON.stringify(params));
  }

  const onResetSearch = (search) => {
    setSearch(search);
    // setPage(1); ?
    
    dispatch(
      clearData({
      })
    );
  
  }
  
  const fullDownloadAllowed = true; // ['admin', 'label_admin'].includes(user?.role);
  
  const exportButtonMenu = <Menu
      onClick={(e)=>{
        if (e.key === 'page') {
          pageButtonRef.current.link.click()
        }

        if (e.key === 'all') {
          getFullData()
        }
      }}
      items={[
        {
          label: 'Export Current Screen',
          key: 'page',
        },
        {
          label: 'Export All',
          key: 'all',
        }
      ]}
    />;
    
  const exportedCSVFile = `Overview-${dayjs().format('YYYY-MM-DD')}.csv`;

  return (<>
    {/* <PageHeader title="Stock and Sales Dashboard" /> */}
    <Breadcrumbs />
    <PageHeader title="Stock and Sales Dashboard">
    </PageHeader>
    <Flex className={css.root} vertical>   

      {/*
      <Form
        name="searchForm"
        style={{ marginTop: 10, marginBottom: 0 }}
        initialValues={{ remember: true }}
        autoComplete="off"
      >
        <Row gutter={16} className={css.searchFormContainer}>
          <Col md={24} lg={18} xl={16}>
            <Form.Item style={{ marginBottom: 10, maxWidth: 266 }} name="name">
              <Input
                defaultValue={search}
                size="large"
                placeholder="Search..."
                style={{ color: "#737373" }}
                prefix={<IconSearch />}
                onChange={handleInputChange}
              />
            </Form.Item>
          </Col>
          <Col md={24} lg={6} xl={8} className={css.actionsWrapper}>
          </Col>
        </Row>
      </Form>
      */}
      <div className={"ibox-holder"}>
        <SearchBox onApply={onApplySearch} initialValues={initialSearchValues} onReset={onResetSearch} />
      </div>
      
      <div className={"ibox-holder"}>
        <SectionHeader title="Search Table">
          <CSVLink data={rows || []} filename={exportedCSVFile} ref={pageButtonRef} >
          </CSVLink>
          <CSVLink data={overviewExport || []} filename={exportedCSVFile} ref={exportButtonRef} >            
          </CSVLink>
          <Space wrap>
            {fullDownloadAllowed ? <Dropdown
                overlay={exportButtonMenu}>
                <Button 
                icon={getOverviewExportInProgress ? <Spin size="small" /> : <IconDownload />}
                />
              </Dropdown>
            : <Button
                onClick={()=>pageButtonRef.current.link.click()} 
                icon={<IconDownload />}
                children='Export Current Screen'
              />
          }
          </Space>

          
        </SectionHeader>
		<WarningMessage />
        <Form name="tableForm" form={form} component={false}>
          <Table
            components={{
              header: {
                row: HeaderCell,
              },
            }}
            columns={columns}
            rowKey={(record) => record.id}
            onRow={onRow}
            dataSource={rows}
            pagination={{
              position: ["bottomRight"],
              responsive: true,
              // onChange: (page) => onQueryParamsChange({ page }),
              current: page,
              total: totalPages,
              pageSize: pageSize,
              hideOnSinglePage: false,
              showSizeChanger: true,
              itemRender: (page, type, originalElement) => {
                if (totalPages <= pageSize && ['page', 'prev', 'next'].includes(type)) {
                  return null;
                }
                return originalElement;
              },
            }}
            loading={getOverviewInProgress}
            onChange={handleTableChange}
            style={{ marginTop: 10 }}
            scroll={{ x: "100%" }}
            tableLayout="fixed"
            locale={{ emptyText: 'Enter your search parameters to start' }} 
          />
        </Form>
      </div>
    </Flex>
  </>
  );
};
